import { useMemo } from "react";
import { AtSign, FileUserIcon, Phone } from "lucide-react";
import { DevicePhoneMobileIcon } from "@heroicons/react/24/outline";

import { clsxMerge, getFullEntityName, fixURL } from "shared/lib/helpers";
import CopyToClipboard from "shared/ui/copy-to-clipboard";
import { LogoLinkedInIcon } from "shared/ui/icons";
import { ContactDetailI } from "shared/lib/interfaces/account";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";
import { Tooltip, TooltipContent, TooltipTrigger } from "shared/ui/tooltip";
import {
  PROSPECT_DISPOSITION_TYPES,
  PROSPECT_DISPOSITION_TYPES_MAP,
  PROSPECT_SYSTEM_DISPOSITION_TYPES,
  PROSPECT_SYSTEM_DISPOSITION_TYPES_MAP,
} from "shared/lib/constants/dispositions";
import { createDisqualificationTooltipContent } from "shared/ui/account-details/disqualification-tooltip-content";
import { CONTACT_TYPES_LABEL_MAP } from "shared/lib/constants/lists";

import { PhoneLink } from "@/modules/pipeline/account-details/shared/phone-link";
import { ExpandableCustomField } from "@/modules/pipeline/account-details/sections/leads-section/expandable-custom-field";
import { getLeadStateFlags } from "@/modules/pipeline/account-details/utils";
import { ProspectTag } from "@/modules/pipeline/account-details/shared/prospect-tag";

const COPY_VALUES_CLASSNAMES = clsxMerge(
  "typography-body-4",
  "line-clamp-2 text-ellipsis break-words"
);

interface ProspectInfoBlockPropsI extends PropsWithClassNameI {
  contact: ContactDetailI;
  hideTags?: boolean;
  hideName?: boolean;
  hideTitle?: boolean;
  isTitles?: boolean;
  hideIcons?: boolean;
  hideTitles?: boolean;
  disablePhones?: boolean;
}

export const ProspectInfoBlock = ({
  className,
  contact,
  hideTags,
  hideName,
  hideTitle,
  hideIcons,
  hideTitles = true,
  disablePhones,
}: ProspectInfoBlockPropsI) => {
  const { isLeadDisqualified, isLeadMarkedDNC, isLeadSuspended } = useMemo(
    () => getLeadStateFlags(contact),
    [contact?.contact_disposition_type]
  );

  const linkedin = useMemo(
    () => contact?.linkedin || contact?.linkedin_url,
    [contact]
  );

  const meta = useMemo(() => {
    return contact?.meta?.values;
  }, [contact]);

  return (
    <div className={clsxMerge("flex flex-1 flex-col gap-y-0.5", className)}>
      {!hideName && (
        <span
          className={clsxMerge(
            "line-clamp-2 max-w-[240px] text-ellipsis typography-body-4-semibold",
            {
              "line-through":
                isLeadDisqualified || isLeadMarkedDNC || isLeadSuspended,
            }
          )}
        >
          {getFullEntityName(contact)}
        </span>
      )}

      {!hideTitle && (
        <span
          className={clsxMerge(COPY_VALUES_CLASSNAMES, "text-[#666]", {
            "flex flex-col items-start": !hideTitles,
          })}
        >
          {!hideTitles && (
            <span className="text-[#666] typography-body-4-medium">Title</span>
          )}

          <span
            className={clsxMerge(COPY_VALUES_CLASSNAMES, {
              "text-black": !hideTitles,
            })}
          >
            {contact.title}
          </span>
        </span>
      )}

      {contact?.email && (
        <CopyToClipboard textToCopy={contact.email}>
          <div
            className={clsxMerge("flex items-center gap-x-3", {
              "flex-col items-start": !hideTitles,
            })}
          >
            {!hideTitles && (
              <span className="text-[#666] typography-body-4-medium">
                Email
              </span>
            )}
            {!hideIcons && (
              <AtSign className="size-[14px] rounded bg-black/10 p-0.5" />
            )}
            <span className={clsxMerge(COPY_VALUES_CLASSNAMES, "ae-link")}>
              {contact.email}
            </span>
          </div>
        </CopyToClipboard>
      )}

      {contact?.phone && (
        <div
          className={clsxMerge("flex items-center gap-x-3", {
            "flex-col items-start": !hideTitles,
          })}
        >
          {!hideTitles && (
            <span className="text-[#666] typography-body-4-medium">Phone</span>
          )}

          {!hideIcons && (
            <Phone className="size-[14px] rounded bg-black/10 p-0.5" />
          )}
          <PhoneLink
            contactId={contact?.id}
            phoneNumber={contact.phone}
            className={COPY_VALUES_CLASSNAMES}
            isDNC={contact?.phone_v2?.do_not_call}
            isLoadingComponent
            disabled={disablePhones}
          />
        </div>
      )}

      {contact?.mobile && (
        <div
          className={clsxMerge("flex items-center gap-x-3", {
            "flex-col items-start": !hideTitles,
          })}
        >
          {!hideTitles && (
            <span className="text-[#666] typography-body-4-medium">Mobile</span>
          )}

          {!hideIcons && (
            <DevicePhoneMobileIcon className="size-[14px] rounded bg-black/10 p-0.5" />
          )}
          <PhoneLink
            contactId={contact?.id}
            phoneNumber={contact.mobile}
            className={COPY_VALUES_CLASSNAMES}
            isDNC={contact?.mobile_v2?.do_not_call}
            isLoadingComponent
            disabled={disablePhones}
          />
        </div>
      )}

      {linkedin && (
        <div
          className={clsxMerge("flex items-center gap-x-3", {
            "flex-col items-start": !hideTitles,
          })}
        >
          {!hideTitles && (
            <span className="text-[#666] typography-body-4-medium">
              LinkedIn
            </span>
          )}

          <a
            target="_blank"
            rel="noreferrer"
            href={fixURL(linkedin)}
            className={clsxMerge(
              COPY_VALUES_CLASSNAMES,
              "ae-link flex items-center"
            )}
          >
            <span className="flex items-center gap-x-3">
              {!hideIcons && (
                <LogoLinkedInIcon className="size-[14px] rounded bg-[#3471FE] p-[3px] text-white" />
              )}
              {linkedin}
            </span>
          </a>
        </div>
      )}

      {/* Person Location */}
      {(contact.person_city ||
        contact.person_state ||
        contact.person_country ||
        contact.person_postal_code) && (
        <div className="relative z-[1] flex w-full gap-x-3">
          {!hideIcons && (
            <FileUserIcon className="relative top-1 size-[14px] min-w-[14px] rounded bg-black/10 p-0.5" />
          )}
          <CopyToClipboard
            textToCopy={[
              contact.person_city,
              contact.person_state,
              contact.person_country,
              contact.person_postal_code,
            ]
              .filter(Boolean)
              .join(", ")}
          >
            <span className={clsxMerge(COPY_VALUES_CLASSNAMES, "text-[#333]")}>
              <span className="mr-1 typography-body-4-medium">Location:</span>
              <span className="[word-break:break-word]">
                {[
                  contact.person_city,
                  contact.person_state,
                  contact.person_country,
                  contact.person_postal_code,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </span>
            </span>
          </CopyToClipboard>
        </div>
      )}

      {/* First Ingestion Source */}
      {contact.first_ingestion_name && (
        <div className="relative z-[1] flex w-full gap-x-3">
          {!hideIcons && (
            <FileUserIcon className="relative top-1 size-[14px] min-w-[14px] rounded bg-black/10 p-0.5" />
          )}
          <span className={clsxMerge(COPY_VALUES_CLASSNAMES, "text-[#333]")}>
            <span className="mr-1 typography-body-4-medium">
              First Ingestion:
            </span>
            <span className="[word-break:break-word]">
              {contact.first_ingestion_name}
            </span>
          </span>
        </div>
      )}

      {/* Latest Ingestion Source */}
      {contact.latest_ingestion_name && (
        <div className="relative z-[1] flex w-full gap-x-3">
          {!hideIcons && (
            <FileUserIcon className="relative top-1 size-[14px] min-w-[14px] rounded bg-black/10 p-0.5" />
          )}
          <span className={clsxMerge(COPY_VALUES_CLASSNAMES, "text-[#333]")}>
            <span className="mr-1 typography-body-4-medium">
              Latest Ingestion:
            </span>
            <span className="[word-break:break-word]">
              {contact.latest_ingestion_name}
            </span>
          </span>
        </div>
      )}

      {meta?.map((metaValue, i) => (
        <div key={i} className="relative z-[1] flex w-full gap-x-3">
          {!hideIcons && (
            <FileUserIcon className="relative top-1 size-[14px] min-w-[14px] rounded bg-black/10 p-0.5" />
          )}
          <ExpandableCustomField lineHeight={18}>
            <span
              className={clsxMerge(
                COPY_VALUES_CLASSNAMES,
                "line-clamp-none text-[#333]"
              )}
            >
              <span className="mr-1 typography-body-4-medium">
                {metaValue.label}:
              </span>
              <span className="[word-break:break-word]">{metaValue.value}</span>
            </span>
          </ExpandableCustomField>
        </div>
      ))}
      {!hideTags && (
        <div className="mt-1 flex flex-wrap gap-1.5">
          {contact.is_primary_lead && (
            <Tooltip showDelay={50}>
              <TooltipTrigger>
                <ProspectTag
                  tag="Primary Lead"
                  className="bg-[#014DFE] text-white"
                />
              </TooltipTrigger>
              <TooltipContent className="z-50 max-w-[250px] text-center">
                A primary lead is the main contact for an account. They are the
                lead that gets dialed first when calling.
              </TooltipContent>
            </Tooltip>
          )}

          {isLeadDisqualified && (
            <ProspectTag
              tag="Disqualified"
              tooltip={createDisqualificationTooltipContent(
                contact.contact_disposition_type
                  ? CONTACT_TYPES_LABEL_MAP[
                      contact.contact_disposition_type as keyof typeof CONTACT_TYPES_LABEL_MAP
                    ]
                  : null,
                contact.dq_reason_notes
              )}
            />
          )}

          {isLeadSuspended && (
            <ProspectTag
              tag={
                PROSPECT_SYSTEM_DISPOSITION_TYPES_MAP[
                  PROSPECT_SYSTEM_DISPOSITION_TYPES.SUPPRESSED
                ]
              }
            />
          )}

          {!isLeadDisqualified &&
            !isLeadSuspended &&
            contact?.contact_disposition_type && (
              <ProspectTag
                tag={
                  PROSPECT_DISPOSITION_TYPES_MAP[
                    contact?.contact_disposition_type as keyof typeof PROSPECT_DISPOSITION_TYPES_MAP
                  ]
                }
                tooltip={
                  contact?.contact_disposition_type ===
                    PROSPECT_DISPOSITION_TYPES.DO_NOT_CALL &&
                  contact.dq_reason_notes &&
                  createDisqualificationTooltipContent(
                    undefined,
                    contact.dq_reason_notes
                  )
                }
              />
            )}
        </div>
      )}
    </div>
  );
};
